// @file Global state for SurfaceGroupPicker.
//
// Manages the group picker modal's visibility and provides a promise-based API
// for awaiting user selection.
//
// Usage:
//   const result = await useSurfaceGroupPickerStore().show()
//   if (result) {
//     const { groupId } = result // section id, color id, or property value
//   }
//   // result is null if user cancelled
import { __ } from '@@/bits/intl'
import type { WallGroupPost } from '@@/types'
import { defineStore } from 'pinia'
import { ref } from 'vue'

const DEFAULT_TITLE = __('Pick a section to post to')
export const useSurfaceGroupPickerStore = defineStore('surfaceGroupPicker', () => {
  const isVisible = ref(false)
  const title = ref<string>(DEFAULT_TITLE)
  const excludedGroupIds = ref<WallGroupPost['id'][]>([])
  let resolvePromise: ((result: { groupId: WallGroupPost['id'] } | null) => void) | null = null

  function show(
    customTitle = DEFAULT_TITLE,
    options: { excludedGroupIds?: WallGroupPost['id'][] } = {},
  ): Promise<{ groupId: WallGroupPost['id'] } | null> {
    // there should only be one instance of the group picker visible at a time,
    // so if it's already opened with a pending promise, throw an error
    if (resolvePromise !== null) {
      throw new Error('Group picker is already visible')
    }
    isVisible.value = true
    title.value = customTitle
    excludedGroupIds.value = options.excludedGroupIds ?? []
    return new Promise((resolve) => {
      resolvePromise = resolve
    })
  }

  function cleanup() {
    isVisible.value = false
    title.value = DEFAULT_TITLE
    excludedGroupIds.value = []
    resolvePromise = null
  }

  function pickGroup(groupId: WallGroupPost['id']) {
    resolvePromise?.({ groupId })
    cleanup()
  }

  function cancel() {
    resolvePromise?.(null)
    cleanup()
  }

  return { isVisible, title, excludedGroupIds, show, pickGroup, cancel }
})
